import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PageHeader, StatusPill } from "@/components/common";
import { api, errMsg, fmtDate } from "@/lib/api";

const ExtendButton = ({ sub, onDone }) => {
  const [days, setDays] = useState(30);
  const extend = async () => {
    try {
      await api.post(`/admin/subscriptions/${sub.id}/extend`, { days: Number(days) });
      toast.success(`Extended by ${days} days`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Popover>
      <PopoverTrigger asChild><Button size="sm" variant="outline" data-testid={`sub-extend-${sub.id}`}>Extend</Button></PopoverTrigger>
      <PopoverContent className="w-56 space-y-2">
        <Input type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)} data-testid="sub-extend-days-input" />
        <Button size="sm" className="w-full" onClick={extend} data-testid="sub-extend-confirm">Extend {days} days</Button>
      </PopoverContent>
    </Popover>
  );
};

export default function Subscriptions() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/subscriptions").then((r) => setRows(r.data));

  useEffect(() => {
    load();
  }, []);

  const cancel = async (id) => {
    if (!window.confirm("Cancel this subscription? Subscription-based contact access ends immediately.")) return;
    try {
      await api.post(`/admin/subscriptions/${id}/cancel`);
      toast.success("Subscription cancelled");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Access" title="Subscriptions" subtitle="Subscriptions expire automatically at their end date. Activate new ones from an agency's Manage panel." />
      <div className="bg-card rounded-xl border overflow-x-auto fade-up">
        <Table data-testid="admin-subscriptions-table">
          <TableHeader><TableRow><TableHead>Agency</TableHead><TableHead>Plan</TableHead><TableHead>Start</TableHead><TableHead>End</TableHead><TableHead>State</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {rows.map((s) => (
              <TableRow key={s.id} data-testid={`sub-row-${s.id}`}>
                <TableCell className="font-medium">{s.agency_name}</TableCell>
                <TableCell>{s.plan_name}</TableCell>
                <TableCell className="text-sm">{fmtDate(s.start_at)}</TableCell>
                <TableCell className="text-sm">{fmtDate(s.end_at)}</TableCell>
                <TableCell><StatusPill status={s.state} testId={`sub-state-${s.id}`} /></TableCell>
                <TableCell className="whitespace-nowrap">
                  {s.status === "active" && <div className="flex gap-2"><ExtendButton sub={s} onDone={load} /><Button size="sm" variant="ghost" className="text-destructive" onClick={() => cancel(s.id)} data-testid={`sub-cancel-${s.id}`}>Cancel</Button></div>}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No subscriptions yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
